// The editor only asks for the presentation once it has mounted, and it mounts behind the App
// gate (auth flags and feature settings), then a lazy chunk, then (in the PowerPoint add-in)
// Office. Nothing in that wait shapes the request: the detail endpoint and its params depend only
// on the user's flags (api/presentation.js getPresentationDetails). So the editor routes start the
// detail and attribute fetches from their route guard, which runs before the gate, as soon as the
// auth flags are in. The editor's own calls go through the same optimizeEditorLoadModule cache, so
// they pick up the in-flight requests instead of fetching again; past the cache window they simply
// refetch. PresentationRedirectForPresenter's warm-up hits the same cache entries.
//
// Biggest win in the add-in, where every slide is its own page booting the whole app, several at
// once when a deck opens. On the web it is a cold load (link, refresh, new tab) that gains.

// Past this, the editor is about to fetch on its own anyway.
const AUTH_FLAGS_WAIT_MS = 3000

const prefetch = (store, presentationId, isAdmin) => {
  const maxAgeInSeconds = store.getters['optimizeEditorLoadModule/maxPresentationCacheTimeInSeconds']
  // Failures are left to the editor's own fetch, which retries past the failed cache entry and
  // owns the error handling.
  store.dispatch('optimizeEditorLoadModule/getPresentationDetailsWithCacheAction', {
    presentationId,
    // Same payload PresentationEditor.getPresentationData sends.
    payloadDataPresentation: { admin: store.state.isAdmin || isAdmin },
    maxAgeInSeconds,
  }).catch(() => {})
  store.dispatch('optimizeEditorLoadModule/getPresentationAttributesWithCacheAction', {
    presentationId,
    maxAgeInSeconds,
  }).catch(() => {})
}

// Takes the store rather than importing it: the router uses this, and the store already imports
// the router (vuex/actions/folders).
const prefetchEditorPresentation = (store, presentationId, { isAdmin = false } = {}) => {
  if (!presentationId) return

  if (store.state.featureToggles.hasLoadedAuthFlag) {
    prefetch(store, presentationId, isAdmin)
    return
  }
  let timer = null
  const unwatch = store.watch((state) => state.featureToggles.hasLoadedAuthFlag, (loaded) => {
    if (!loaded) return
    clearTimeout(timer)
    unwatch()
    prefetch(store, presentationId, isAdmin)
  })
  timer = setTimeout(unwatch, AUTH_FLAGS_WAIT_MS)
}

// beforeEnter for the editor routes. Never holds the navigation up. Only fires on entering the
// route, not on moving between decks inside it; those keep the editor's own fetch.
//
// Takes a getter, not the store: the route table is built while the store is still being
// imported (the store imports the router), so the store only exists once a navigation runs.
export const prefetchEditorPresentationOnEnter = (getStore) => (to, _from, next) => {
  // Never let the prefetch break the navigation; the editor fetches on its own anyway.
  try {
    // App.vue sets state.isAdmin from the same ?admin param, but only once it has mounted.
    prefetchEditorPresentation(getStore(), to.params.id, { isAdmin: !!to.query.admin })
  } catch (e) {
    // Nothing to do: the editor's own fetch follows.
  }
  next()
}
